<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import ChangeInventoryTable from '@/components/ChangeInventoryTable.vue';
import ChangeStepForm from '@/components/ChangeStepForm.vue';
import RecordChildList from '@/components/RecordChildList.vue';
import { useChangeChecklist } from '@/composables/useChangeChecklist';
import { confirmAction, promptAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage } from '@/utils/format';

/**
 * 工程变更：五步框架 + 每步的任务清单（0822 Tony）。
 *
 * 五步照《工程变更控制程序》7.1–7.5，定义在后端（门禁、体检将来认同一份）。
 * 两条硬规矩在后端把着，这里只负责让人看见和操作：
 * - **步骤是独立事实**，不从单据状态推（第 1、2 步都发生在 submitted 下，推不出来）；
 * - **推进的判据是任务勾完**，没铺任务的步骤放行。
 *
 * 0824 起这块是变更的**唯一端口**：抽屉顶上的「下一步」提示条、
 * 抽屉底下另挂的那份执行清单、状态区的推进类按钮都撤了，全收到这里——
 * 同一件事有两个入口，人就会在两边各做一半。
 */
const props = defineProps<{
  changeId: number | string | null;
  /** 责任人下拉：本项目团队，和问题清单用同一份 */
  people?: string[];
  /** 只读账号只看进度，不给写入类按钮（生成清单、催办、铺任务、加任务、勾选、删） */
  writable?: boolean;
  /**
   * 外层记录变了就换个值，这块重读。
   * 原来只 watch changeId：在同一条记录上点完状态按钮，五步条不刷新，
   * 看着还停在旧的一步（0824）。
   */
  refreshKey?: number | string | null;
  /**
   * 改措施状态前先让外层把抽屉表单里没保存的改动存了。
   * 执行清单从抽屉底下搬到第 4 步里来，这个勾子必须跟着搬——
   * 「断点实施」要求父变更的「实际断点」非空，人填了没存就点完成，
   * 后端读库是空的却报「还是空的」（试用反馈 0819）。丢了就是把那个坑挖回来。
   */
  beforeChildAction?: () => Promise<boolean>;
  /**
   * 选中的这条变更记录（0826）：第 3 步「客户批准 / 告知记录」、第 4 步「断点与标识」
   * 的当前值从它的 normalized 里读——通用字段区删掉后，这几格门禁要读的字段搬进了对应步骤。
   */
  record?: ModuleRecord | null;
  /** 新建态第 1 步表单的「关联项目」下拉选项（模块中心传；项目页不传=建单自动挂当前项目） */
  projectOptions?: Array<{ value: string; label: string }>;
  /** 新建态：父层正在建单（创建→传附件→提交第 1 步），表单按钮跟着禁用 */
  creating?: boolean;
  /**
   * 台账费用列对当前账号可不可见（0826 Tony：变更费用仅本项目项目经理和管理员）。
   * 由视图层从 ledger-extras 拿来传下——第 5 步的「台账与费用」块按它显隐；
   * 真正的锁在后端（读裁剪 + 写门禁），这里只是不给看不见的人摆一排必然 403 的格子。
   */
  costVisible?: boolean;
  /**
   * 这条变更挂了几份资料（0828 关闭确认要看）。
   * 由视图层从记录里拿来传下；不传就当 0——宁可提醒人去传，不装作有。
   */
  attachmentCount?: number;
}>();

const emit = defineEmits<{
  /** 清单/步骤动过了，外层重新拉列表和记录（推进现在同时会改 status） */
  changed: [];
  /** 新建态第 1 步表单点了提交：值和凭证文件交给外层建单 */
  create: [payload: {
    values: Record<string, string>;
    file: File | null;
    /** 新建态贴在「客户的变更要求」里的截图（0911） */
    customerImages?: File[];
  }];
  /*
    作废这条变更（0828 Tony:「这个状态操作是不是重复了啊」）。

    原来「作废」孤零零挂在抽屉最下面的「状态操作」里，
    而「中途关闭」在第 5 步的卡片里——两个都是「这条变更不往下走了」，
    摆在两个地方，看着就像重复。现在并排放，各写一句什么时候用哪个。
    真正的动作还是外层那套状态流转，这里只把入口搬过来。
  */
  void: [];
}>();

interface Task {
  id: number; stepOrder: number; title: string;
  ownerName: string | null; dueAt: string | null;
  doneAt: string | null; doneBy: string | null; source: string;
  /** 审批的结论：approved / rejected；普通任务没有（0827） */
  conclusion?: string | null;
  /**
   * 这一条我能不能勾 / 批（0929 后端算好给的）：项目组里的人照常；
   * 不在项目组的人只有点名给他的那一条能动；审批行指定了人就只有本人或管理员能批。
   */
  canAct?: boolean;
}
interface Step {
  order: number; code: string; name: string; owner: string; exit: string;
  /** 这一步该干什么。原来按单据状态取（第 1、2 步都在 submitted 下说同一句话），现在按步取 */
  hint: string;
  /**
   * 仅第 3 步返回（0825）：本单据的审批链角色，按「量产前后 × 变更等级」四场景
   * 由后端路由出来。静态定义接口没有这个字段——路由要看单据本身。
   */
  route?: string[];
  /** 任务还是旧链、路径已是新链（有人批过所以不敢自动重铺） */
  routeStale?: boolean;
  /** 第 4、5 步为 true：任务就是执行清单（ecm_change_actions）本身 */
  fromChecklist: boolean;
  /** 仅 fromChecklist 步返回：清单按什么分组显示 */
  groups?: Array<{ name: string; codes: string[] }>;
  /**
   * 仅兜底的那个清单步返回：**不在现行清单口径内**的措施条数
   * （旧 EQS 迁来、没有动作编号的那批）。
   *
   * 0827 Tony 把「其他措施」那一组撤了。它们不显示、不计进度、不挡推进，
   * 但条数要照实说一句——静悄悄地少掉一批数据，才是这套系统真正吃过亏的事。
   */
  legacy?: number;
  /**
   * 属于**别的清单步**的措施 code：这一屏整个不显示。
   * 不排除的话，第 5 步的 ST01–ST06 会掉进第 4 步的「其他措施」，
   * 同一批措施两步各显示一遍（0827 Tony 截图，差点被当成垃圾数据删掉）。
   */
  foreignCodes?: string[];
  /**
   * 这一步「一键铺标准任务」铺得出东西来（后端按 taskHints 判）。
   * 第 1 步（表单就是必填项）和第 4 步（任务即执行清单）铺出来是 0 条，
   * 按钮不摆；第 5 步铺的是程序 7.5 的 5 条关闭任务，照旧摆。
   */
  seedable?: boolean;
  /**
   * 仅清单步：这一步被设成「不适用」的措施条数。作废的不进分子分母，
   * 所以一组全设成不适用时 total 是 0——没有这一格就只能说成
   * 「执行清单还没生成」，而清单明明生成了，只是这一组这次都不用做。
   */
  voided?: number;
  total: number; done: number; tasks: Task[];
}

/** 照抄原来抽屉 drawerChildren 里的三个字段，搬过来别丢 */
const ACTION_EXTRA_FIELDS = { bgnr: '变更内容', gnyx: '功能影响', exgf: '外观影响' };

const steps = ref<Step[]>([]);
const currentStep = ref(1);
/** 单据状态：0827 起「关闭」是站在第 5 步上的独立动作，按钮要按它决定摆不摆 */
const status = ref<string | null>(null);
const openStep = ref<number | null>(null);
/*
  现在推不动的原因（0828 Tony:「希望点进去能够看到卡住的原因，而且能够明显一点的」）。

  这些话原来只在点了推进之后弹一下错就没了——人看着一步「已完成 · 待推进」，
  不点一次不知道自己卡在哪。后端读的时候就算好，这里摆成红框。
  判据是后端复用推进那几个 assert 得来的，不是前端另猜一套。
*/
const blockers = ref<string[]>([]);
/**
 * 被驳回的那一下：谁、什么时候、第几级、为什么（0920 Tony:「驳回理由也需要同步显示」）。
 *
 * 理由一直存在库里，但从来没返回给前端——界面上只有一个红色的「已驳回」，
 * 为什么驳的只有驳的人自己知道，申请人得去翻站内信。
 */
const rejection = ref<{
  reason: string; by: string | null; at: string | null; level: string | null;
  canResubmit: boolean;
} | null>(null);
const resubmitting = ref(false);

/*
  这张单我能不能改（0929 方案 B：按变更挂的项目判，后端和写接口同一个判据算好给）。

  原来这里只认 props.writable——那是**账号**能不能写，不管这张单挂在哪个项目，
  于是不在项目组的人看到的是一排亮着的按钮，而后端 0929 起会拒。
  缺省可写（!== false）：总览还在路上时按钮不该先灰一下再亮起来。
*/
const access = ref<{ writable: boolean; reason: string | null } | null>(null);
const canEdit = computed(() => !!props.writable && access.value?.writable !== false);

/** 这一条能不能勾 / 批：后端给了 canAct 就照它，没给（老接口）退回整张单的口径 */
function canActOn(task: Task): boolean {
  if (!props.writable) return false;
  return task.canAct ?? canEdit.value;
}

/** 只能看的原因，摆在五步条顶上；能改、或者还是新建态时为空 */
const readonlyNote = computed(() => (
  !isDraft.value && access.value?.writable === false ? access.value.reason ?? '' : ''));

/*
  客户批准 / 告知记录那一格可以传截图和文件（0828 Tony）。

  A 级要的是「客户书面批准」——那本来就是一封邮件的截图或一个 PDF。
  只让人打字描述「邮件主题+日期」，证据不在系统里，
  而这一格正是进第 4 步的门禁，审计追的也是它。
  粘贴、拖拽、选文件三种都收，走的是这条变更的业务附件同一个口子（打归属标记）。
*/
const attachBusy = ref(false);
const attachNote = ref('');
const attachInput = ref<HTMLInputElement | null>(null);

function pastedName(file: File, index: number): string {
  if (file.name && file.name !== 'image.png') return file.name;
  const stamp = new Date().toISOString().replace(/[-:T]/gu, '').slice(0, 14);
  const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
  return `客户批准-${stamp}${index ? `-${index + 1}` : ''}.${ext}`;
}

/**
 * 这一格传的文件打这个归属标记（0920 Tony:「拖拽进去的文件直接去下面业务附件中，
 * 但是 choose file 是在这个红框里面显示的，而且看不到」）。
 *
 * 原来传上去**不带 note**：文件混进底下那一长串业务附件里，
 * 和第 1 步的客户要求原文、可行性方案的文件挤在一起分不出谁是谁；
 * 这一格自己下面又什么都不列，只有一句会消失的「已存 N 个文件」。
 * 人传完看不见，只能以为没传上。
 *
 * 客户要求那一格（ChangeStepForm 的 CUSTOMER_ATTACH_NOTE）0911 就是这么做的，
 * 这里照抄同一套：打标记 + 就地列出来。
 */
const AUTH_ATTACH_NOTE = '客户批准 / 告知记录';

async function attachFiles(files: File[]): Promise<void> {
  if (props.changeId === null || !files.length || attachBusy.value) return;
  attachBusy.value = true;
  attachNote.value = '';
  try {
    for (const [index, file] of files.entries()) {
      const payload = new FormData();
      payload.append('file', file, pastedName(file, index));
      payload.append('note', AUTH_ATTACH_NOTE);
      await eqsApi.uploadModuleAttachment('changes', 'changes', Number(props.changeId), payload);
    }
    notify('success', files.length > 1 ? `已存 ${files.length} 个文件` : '已存入', '就列在这一格下面。');
    await loadAuthAttachments();
  } catch (error) {
    notify('error', '没存上', errorMessage(error));
  } finally {
    attachBusy.value = false;
    if (attachInput.value) attachInput.value.value = '';
  }
}

/** 这一格挂着的证据文件；空数组 = 一个都没传 */
const authAttachments = ref<Array<{
  id: number; fileName: string; sizeBytes: number; uploadedBy?: string;
  canRemove?: boolean; removeBlockedReason?: string | null;
}>>([]);
const authRemoving = ref<number | null>(null);

async function loadAuthAttachments(): Promise<void> {
  if (props.changeId === null) { authAttachments.value = []; return; }
  try {
    const payload = await eqsApi.moduleAttachments('changes', 'changes', props.changeId);
    authAttachments.value = (payload.attachments ?? [])
      .filter((file) => (file.note ?? '') === AUTH_ATTACH_NOTE);
  } catch {
    // 读不到就当没有：这一格不该因为附件接口挂了而打不开
    authAttachments.value = [];
  }
}

function authAttachmentUrl(attachmentId: number): string {
  return eqsApi.moduleAttachmentDownloadUrl('changes', 'changes', props.changeId ?? 0, attachmentId);
}

/** 传错了要能撤下来（0920 加的撤下能力，这一格也给上） */
async function removeAuthAttachment(file: { id: number; fileName: string; canRemove?: boolean }): Promise<void> {
  if (props.changeId === null || file.canRemove === false) return;
  const reason = await promptAction({
    title: `撤下「${file.fileName}」？`,
    message: '这份材料不会真的删掉，只是从这条变更上撤下来。',
    prompt: '写一句为什么撤（选填，会记进留痕）',
    confirmLabel: '撤下',
  });
  if (reason === null) return;
  authRemoving.value = file.id;
  try {
    await eqsApi.removeModuleAttachment('changes', 'changes', props.changeId, file.id, reason);
    notify('success', '附件已撤下', file.fileName);
    await loadAuthAttachments();
  } catch (caught) {
    notify('error', '撤不下来', errorMessage(caught));
  } finally {
    authRemoving.value = null;
  }
}

/**
 * 0914 Tony 第 7 条:「所有附件都还不能用直接拖拽的方式上传」。
 *
 * 拖拽这边本来就收全部文件（onAuthDrop），**粘贴这边只认 `image/*`**——
 * 从资源管理器复制一封 .msg 的客户邮件再 Ctrl+V，什么都不会发生，
 * 而且一声不吭。客户批准/告知记录十有八九就是一封邮件。
 * 和 ChangeStepForm 里客户要求那一格同一处改动。
 */
function onAuthPaste(event: ClipboardEvent): void {
  const files = [...(event.clipboardData?.items ?? [])]
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
  if (!files.length) return;       // 纯文字粘贴照常进文本框
  event.preventDefault();
  void attachFiles(files);
}

function onAuthDrop(event: DragEvent): void {
  const files = [...(event.dataTransfer?.files ?? [])];
  if (!files.length) return;
  event.preventDefault();
  void attachFiles(files);
}

function onAuthPick(event: Event): void {
  void attachFiles([...((event.target as HTMLInputElement).files ?? [])]);
}
const busy = ref(false);
const newTitle = ref('');
const newOwner = ref('');
const newDue = ref('');

/**
 * 新建时（还没有 changeId）也要看得见五步（0824 Tony：模块中心新建还是老版本）。
 *
 * 原来 changeId 为 null 直接清空、整块不渲染，于是新建屏上只有一句
 * 「这是第 1 步」的文字——同一个功能在新建和编辑两屏长得不一样，
 * 人自然觉得新建那屏没改过。
 *
 * 新建态拿的是**静态定义**（没有任务、没有进度，因为那时确实还没有），
 * 五个格子照常摆出来、第 1 步高亮，让人知道自己站在流程的哪一格；
 * 能点开看每步的「做完的标准」，但推进/铺任务/加任务这些要有单据才能做的一律不给。
 */
const isDraft = computed(() => props.changeId === null);

async function load(): Promise<void> {
  if (props.changeId === null) {
    try {
      const payload = await eqsApi.changeStepDefinitions() as unknown as { steps: Step[] };
      // 静态定义里没有任务和进度，补成空值，模板那边一套渲染逻辑就够用
      steps.value = (payload.steps ?? []).map((step) => ({
        ...step, fromChecklist: false, seedable: false, total: 0, done: 0, tasks: [],
      }));
      currentStep.value = 1;
      status.value = null;
      access.value = null;
      if (openStep.value === null) openStep.value = 1;
    } catch {
      steps.value = [];
    }
    return;
  }
  try {
    const payload = await eqsApi.changeStepOverview(props.changeId) as unknown as {
      currentStep: number; steps: Step[]; status: string | null; blockers?: string[];
      rejection?: {
        reason: string; by: string | null; at: string | null; level: string | null;
        canResubmit: boolean;
      } | null;
      access?: { writable: boolean; reason: string | null };
    };
    steps.value = payload.steps ?? [];
    currentStep.value = payload.currentStep ?? 1;
    status.value = payload.status ?? null;
    blockers.value = Array.isArray(payload.blockers) ? payload.blockers : [];
    rejection.value = payload.rejection ?? null;
    access.value = payload.access ?? null;
    if (openStep.value === null) openStep.value = currentStep.value;
  } catch {
    // 读不到就不显示这块，不该因为一个区块让变更页打不开
    steps.value = [];
    blockers.value = [];
    rejection.value = null;
    access.value = null;
  }
}

/**
 * 改完了，重新提交（0920 Tony:「应该直接退回给申请人」）。
 *
 * 同一条单子改完重走，不再另开新单——追溯链不断：一条变更从提出到最后批准
 * 是一条线，中间被驳回过几次、每次为什么，全留在这条单子上。
 */
async function resubmit(): Promise<void> {
  if (props.changeId === null || resubmitting.value) return;
  if (!await confirmAction({
    title: '改完了，重新提交这条变更？',
    message: '第 3 步的审批会全部清空，从第 1 级重新走一遍。'
      + String.fromCharCode(10)
      + '上一轮谁批过、谁驳的都会留在留痕里，不会消失。',
  })) return;
  resubmitting.value = true;
  try {
    const result = await eqsApi.resubmitChange(props.changeId);
    notify('success', '已重新提交', `这是第 ${result.round} 轮，审批从第 1 级重新开始。`);
    await reload();
  } catch (caught) {
    notify('error', '重新提交失败', errorMessage(caught));
  } finally {
    resubmitting.value = false;
  }
}

/** 清单/任务动过之后：自己重读 + 让外层也重读（步骤数、状态都可能跟着变） */
async function reload(): Promise<void> {
  await load();
  emit('changed');
}

/*
  第 4 步的库存清点表（0826 Tony 手绘表）。

  一张变更一张表、六个位置固定，**只读一次**放在这里，按措施代号分给各行：
  EX03 拿「供应商」那一行、EX12 拿其余五行、ST01–ST06 各拿自己那一行（只读）。
  让每一行自己去拉一次的话，光第 4 步展开就要发 8 个请求拿同一张表。
*/
interface InventoryRow {
  id: number; location: string; itemName: string | null; quantity: number | null;
  /** 处置完还剩多少 / 怎么处置（0827 Tony 的表补的两列，处置那条措施填） */
  remainingQuantity: number | null; disposition: string | null;
  countedByName: string | null; countedAt: string | null;
  countCode: string; disposeCode: string;
}
const inventory = ref<InventoryRow[]>([]);

async function loadInventory(): Promise<void> {
  if (props.changeId === null) { inventory.value = []; return; }
  try {
    const payload = await eqsApi.changeInventory(props.changeId);
    inventory.value = (payload.rows ?? []) as unknown as InventoryRow[];
  } catch {
    // 清点表拉不到不该把整个第 4 步挡住
    inventory.value = [];
  }
}

/** 这条措施该看哪几行：数的看自己数的，处置的看自己那一行 */
function inventoryFor(code: unknown): InventoryRow[] {
  const key = String(code ?? '').trim().toUpperCase();
  if (!key) return [];
  return inventory.value.filter((row) => row.countCode === key || row.disposeCode === key);
}
function inventoryMode(code: unknown): 'count' | 'dispose' {
  const key = String(code ?? '').trim().toUpperCase();
  return inventory.value.some((row) => row.countCode === key) ? 'count' : 'dispose';
}

watch(() => props.changeId, () => {
  openStep.value = null;
  void load();
  void loadInventory();
}, { immediate: true });
watch(() => props.refreshKey, () => { void load(); });

const active = computed(() => steps.value.find((step) => step.order === openStep.value) ?? null);

/**
 * 第 3 步的审批路径一行字（0825 需求 4）。
 * 路由是后端按「量产前后 × 变更等级」算好的，这里只照着显示，不在前端复算——
 * 新建态没有单据也就没有路由（静态定义不带 route），空串 = 不显示。
 */
const routeLine = computed(() => {
  if (isDraft.value) return '';
  const route = active.value?.route;
  return route?.length ? route.join(' → ') : '';
});

/*
  生成执行清单、催办未完成措施：直接用抽屉那套（useChangeChecklist），不重写一份。
  尤其是 remind() 里「几条没写责任人、几条责任人对不上账号」那段——
  催不到谁必须报出来，不能假装都催到了；重写一遍迟早走样。
  那个 composable 认的是整条记录，这里只拿得到 changeId，给它一个只带 id 的壳。
*/
const checklistTarget = ref<ModuleRecord | null>(null);
watch(() => props.changeId, (id) => {
  checklistTarget.value = id === null ? null : ({ id, status: '' } as ModuleRecord);
}, { immediate: true });
const { generating, reminding, generate, remind } = useChangeChecklist(checklistTarget, reload);

/** 当前这一步该干什么。原来是抽屉顶上那条「下一步」提示条，按状态取，现在按步取 */
const currentHint = computed(
  () => steps.value.find((step) => step.order === currentStep.value)?.hint ?? '',
);

/*
  关闭确认（0828 Tony:「我需要变更管理者确认所有任务已完成，资料完整关闭变更流程」）。

  两个勾选是**确认**不是门禁：真正的门禁在后端（第 5 步任务全勾完 + 整份清单有交代
  + 只有变更牵头人能点）。这里让人在按下终局按钮前，把三件事各看一眼。
  勾选不落库——它约束的是「这一下点得算数」，不是一条要留痕的状态。
*/
const vowTasks = ref(false);
const vowFiles = ref(false);

/*
  中途关闭（0828 Tony:「做一个直接关闭按钮，然后可以直接关闭，
  然后把这个变更没完成的也都一起挂出来说明是中途关闭的变更」）。

  和正常关闭是两件事：正常关闭是「走完了」，中途关闭是「不走了」——
  客户撤销、方案推翻、并到别的单子。所以它不走门禁，但**必须写原因**，
  而且按下那一刻会把没做完的东西整份存进单据，台账和详情都看得到。
*/
const earlyOpen = ref(false);
const earlyReason = ref('');

/** 这条单是不是中途关的；是的话把当时没做完的挂出来 */
const earlyClose = computed(() => {
  const bag = (props.record?.normalized ?? {}) as Record<string, unknown>;
  const hit = bag.earlyClose as {
    at?: string; by?: string; atStep?: number; reason?: string;
    unfinished?: Array<{ kind: string; step: number | null; code: string | null; title: string; owner: string | null }>;
  } | undefined;
  return hit && hit.reason ? hit : null;
});

async function closeEarly(): Promise<void> {
  if (props.changeId === null) return;
  const ok = await confirmAction({
    title: '中途关闭这条变更',
    message: '流程没走完就关闭。没做完的任务和措施会原样记进单据，台账上标成「中途关闭」。这一步不可撤销。',
  });
  if (!ok) return;
  await run(() => eqsApi.closeChangeEarly(Number(props.changeId), earlyReason.value.trim()));
  earlyOpen.value = false;
  earlyReason.value = '';
}

/** 变更管理者＝第 1 步表单里的「变更牵头人」；后端按它判谁能关 */
const closeOwner = computed(() => {
  const bag = (props.record?.normalized ?? {}) as Record<string, unknown>;
  const value = props.record?.owner_name ?? props.record?.ownerName ?? bag.ownerName;
  return String(value ?? '').trim();
});

/*
  打开的是不是第 5 步这一格。

  0828 Tony:「我还是没看到确认关闭变更的选项」——他展开了第 5 步，
  但那条单其实还停在第 3 步（三级审批一个都没批），
  关闭卡按 atLastStep 判就整块不渲染，看着像功能没做。
  现在只要打开第 5 步就摆出这张卡；没轮到的时候在卡里说清楚卡在哪，
  **不要什么都不说**——「看不见」和「还没到」在屏幕上长得一模一样。
*/
const onClosePanel = computed(() => openStep.value === steps.value.length && steps.value.length > 0);

/** 第 N 步叫什么；越界就空着，不写「第 undefined 步」 */
function stepName(order: number): string {
  return steps.value.find((step) => step.order === order)?.name ?? '';
}

/** 还没轮到关闭时，说清当前这一步在等谁——只说「没走到」等于让人自己去猜 */
const pendingHint = computed(() => {
  const step = steps.value.find((item) => item.order === currentStep.value);
  if (!step) return '';
  const waiting = step.tasks.filter((task) => !task.doneAt);
  if (waiting.length) {
    const names = [...new Set(waiting.map((task) => task.ownerName).filter(Boolean))];
    return names.length
      ? `这一步还在等：${names.slice(0, 3).join('、')}${names.length > 3 ? ' 等' : ''}。`
      : `这一步还有 ${waiting.length} 项没做完。`;
  }
  if (step.total > step.done) return `这一步的执行清单还有 ${step.total - step.done} 项没交代。`;
  return '这一步做完了，点上面的推进按钮往下走。';
});

/*
  审批要逐级走（0828）。这里算「现在轮到第几级」：
  同级并行会签算一级，整级批完才轮到下一级——和后端 assertPreviousLevelsDone 同一套口径。
  两边算法分家的话，界面上摆着按钮而后端拒绝，或者反过来，都是最难查的那种不一致。
*/
function approvalLevelOf(title: string): number | null {
  const hit = /^【第\s*(\d+)\s*级】/u.exec(String(title ?? ''));
  return hit ? Number(hit[1]) : null;
}

/** 当前该批的那一级；没有待批的审批任务时返回 null */
const waitingLevel = computed(() => {
  const step = steps.value.find((item) => item.order === 3);
  if (!step) return null;
  const pending = step.tasks
    .filter((task) => !task.doneAt)
    .map((task) => approvalLevelOf(task.title))
    .filter((level): level is number => level !== null);
  return pending.length ? Math.min(...pending) : null;
});

/** 这一条轮到了没有：只有当前这一级（含同级会签）能出结论 */
function levelReached(task: { title: string }): boolean {
  const level = approvalLevelOf(task.title);
  if (level === null) return true;
  return waitingLevel.value === null || level <= waitingLevel.value;
}

const closeReady = computed(() => {
  const step = active.value;
  // 任务数从 tasks 数组现算：视图上没有 doneTasks/totalTasks 这两格
  const taskTotal = step?.tasks.length ?? 0;
  const taskDone = step?.tasks.filter((task) => task.doneAt).length ?? 0;
  const tasks = taskTotal === 0 || taskDone >= taskTotal;
  const checklist = !step || step.total === 0 || step.done >= step.total;
  const files = (props.attachmentCount ?? 0) > 0;
  return { tasks, checklist, files, taskDone, taskTotal, all: tasks && checklist && files };
});

/** 差什么就说什么——只说「不能关」等于让人自己去猜 */
const closeBlockers = computed(() => {
  const out: string[] = [];
  if (!closeReady.value.tasks) out.push('第 5 步还有任务没勾完');
  if (!closeReady.value.checklist) out.push('执行清单还有没交代的');
  if (!closeReady.value.files) out.push('还没上传变更资料');
  return out;
});

/** 换了一条变更或换了一步，之前勾的确认作废——不能替下一单点头 */
watch([() => props.changeId, () => props.refreshKey, currentStep], () => {
  vowTasks.value = false;
  vowFiles.value = false;
});

/** 站在最后一步上，按钮就是「关闭这条变更」（0827 Tony：确认完才关闭） */
const atLastStep = computed(() => currentStep.value >= steps.value.length && steps.value.length > 0);
const closed = computed(() => ['closed', 'done', 'completed']
  .includes(String(status.value ?? '').toLowerCase()));

const advanceLabel = computed(() => (atLastStep.value
  ? '关闭这条变更' : `推进到第 ${currentStep.value + 1} 步`));

/** 推进现在也会改 status，按钮上得说清楚——不然人会以为还得再点一次状态按钮 */
const advanceHint = computed(() => (atLastStep.value
  ? '关闭这条变更。要求第 5 步的闭环任务全部勾完，且整份执行清单'
    + '（含库存清点与断点）都做完或设了「不适用」。关闭之后要改得先退回。'
  : `推进到第 ${currentStep.value + 1} 步。`
    + '这一步铺了任务就得先勾完；推进的同时把单据状态改成这一步对应的状态'
    + '（已经更靠后的状态不会被打回）。'));

/**
 * 清单步空着时说清楚下一步做什么，别只写「没有数据」。
 *
 * 0827 起走到第 4 步会**自动**生成（和第 3 步自动铺审批链一个路子），
 * 所以还没走到的时候要说「到时候自动生成」——写成「还没生成，点按钮」
 * 会让人以为缺了东西（Tony 0827：「新建变更里面，第 4 步内容缺失」）。
 */
const checklistEmptyHint = computed(() => {
  if (currentStep.value < 4) {
    return '推进到第 4 步时，系统会按第 2 步勾的影响范围自动生成执行清单，'
      + '不用手工铺。想提前看一眼，也可以现在就点下面的按钮先生成。';
  }
  return active.value?.order === 5
    ? '这条变更还没有执行清单。库存清点与断点这一组跟整份清单一起生成——点下面「✨ 按影响范围生成执行清单」。'
    : '这条变更还没有执行清单。点下面「✨ 按影响范围生成执行清单」，按第 2 步勾的影响范围铺出来，再逐条指派责任人。';
});

/**
 * 每一步在步骤条上长什么样。
 *
 * 0827 Tony：「第四步完成也没有点亮」——第 4 步的措施全做完（或设了不适用）了，
 * 格子还是灰的，因为单据还停在第 3 步。所以多一个 **ready**：
 * **任务做完了、但还没人推进过去**。
 *
 * 为什么不直接标成 done：done 是实心绿，含义是「这一步走过去了」，
 * 库里有 step_advanced 那条审计。做完 ≠ 推进过——一条停在第 3 步的变更
 * 把第 4 步显示成走过了，等于替人点了那个按钮。
 * ready 用绿描边 + 「待推进」四个字：看得出亮了，也看得出还差一下。
 */
function stateOf(step: Step): 'done' | 'current' | 'ready' | 'pending' {
  if (step.order < currentStep.value) return 'done';
  if (step.order === currentStep.value) return 'current';
  return finished(step) ? 'ready' : 'pending';
}

/**
 * 这一步没有欠着的事了。
 *
 * **一条不适用也算有交代**：作废的措施不进分子分母，所以一组措施全设成不适用时
 * total 是 0——只看 total > 0 的话，这种步永远亮不起来，而它恰恰是
 * 已经处理完的那种（线上 ECN-EEE-001 的第 5 步就是 6 条全不适用）。
 * 判据和后端推进门禁一致：没有「既没做完也没设不适用」的条目就算做完。
 */
function finished(step: Step): boolean {
  if (step.done < step.total) return false;
  return step.total > 0 || (step.voided ?? 0) > 0;
}

/** 当前这一步的活干完了：推进按钮从灰按钮变主按钮，别让人对着做完的清单找出口 */
const currentReady = computed(() => {
  const step = steps.value.find((item) => item.order === currentStep.value);
  return !!step && finished(step);
});

async function run(action: () => Promise<unknown>, okText?: string): Promise<void> {
  if (busy.value || props.changeId === null) return;
  busy.value = true;
  try {
    await action();
    await reload();
    if (okText) notify('success', okText, '');
  } catch (caught) {
    notify('error', '没成功', errorMessage(caught));
  } finally {
    busy.value = false;
  }
}

async function seed(step: number): Promise<void> {
  await run(async () => {
    const result = await eqsApi.seedChangeStep(props.changeId!, step) as {
      created?: number; assigned?: number;
      unresolved?: Array<{ role: string; reason: string }>;
    };
    if (!result.created) {
      notify('info', '这一步的标准任务已经铺过了', '要加别的任务用下面那行。');
      return;
    }
    /*
      第 3 步铺的是审批链，0825 起自动派人（项目团队 / 钉钉直属主管 / 职务匹配）。
      **没派上的必须点名说原因**——「质量经理有两位请手工指定」和一片安静的空责任人，
      对用的人是两个世界；照实念，别让人自己发现哪级漏了。
    */
    if (step === 3 && result.assigned !== undefined) {
      const misses = result.unresolved ?? [];
      if (!misses.length) {
        notify('success', `审批链已铺好，${result.assigned} 级全部自动指派`, '派错了直接改任务的责任人。');
      } else {
        notify('info', `审批链已铺好，自动指派 ${result.assigned}/${result.created} 级`,
          misses.map((item) => `${item.role}：${item.reason}`).join('；'));
      }
    }
  });
}

async function toggle(task: Task): Promise<void> {
  await run(() => eqsApi.updateChangeTask(props.changeId!, task.id, { done: !task.doneAt }));
}

async function addTask(step: number): Promise<void> {
  const title = newTitle.value.trim();
  if (!title) return;
  await run(async () => {
    await eqsApi.addChangeTask(props.changeId!, {
      stepOrder: step, title, ownerName: newOwner.value || null, dueAt: newDue.value || null,
    });
    newTitle.value = '';
    newOwner.value = '';
    newDue.value = '';
  });
}

/* ---------- 第 3 步的审批行：通过 / 驳回（0827 Tony） ---------- */
/*
  审批不是「勾个框」：它有两个结果，而且只有轮到的那个人能出结论。
  一个谁都能勾的框，审计问「谁批的」时答不上来。
  真锁在后端（不是本人直接报错），这里只是别把点不动的按钮摆给人看。
  「是不是本人」0929 起由后端算进每条的 canAct（canActOn），这里不再拿姓名另比一遍。
*/

/** 【第 N 级】开头的才是审批行 */
function isApproval(task: Task): boolean {
  return active.value?.order === 3 && /^【第\s*\d+\s*级】/u.test(task.title ?? '');
}

/** 轮到了、但不归我批：说清在等谁，别摆两颗点了才报错的按钮 */
function approvalWaiting(task: Task): string {
  const owner = (task.ownerName ?? '').trim();
  return owner ? `等${owner}批` : '还没指定审批人（项目组里的人可以先把责任人填上）';
}

async function approve(task: Task): Promise<void> {
  await run(() => eqsApi.updateChangeTask(props.changeId!, task.id, { done: true }));
}

async function reject(task: Task): Promise<void> {
  /*
    话术跟 0920 的口径走：驳回 = 退回给申请人改完重交，不再是「存档、另开新单」。
    这两句一直停在 0827 的说法上（0929 才发现），审批人照着读会以为驳一下单子就死了。
  */
  /*
    1031：原来是原生 prompt 写理由、再弹一次确认，两道框。原生 prompt 只有一行，
    标题栏写的是服务器 IP，钉钉内置浏览器里还可能直接被拦。并成一个应用内弹窗：
    多行理由、必填（没填关不掉）、红色主按钮，焦点先落在理由框。
  */
  const reason = await promptAction({
    title: `驳回「${task.title}」？`,
    message: '整条审批链到此为止，单子退回给申请人改：他会收到通知和你写的理由，改完重新提交，审批从第 1 级重新走。',
    prompt: '驳回理由（必填，申请人照着改）',
    promptPlaceholder: '驳了不说为什么，发起人只能猜着重提一遍',
    promptRequired: true,
    danger: true,
    confirmLabel: '驳回，退回申请人',
  });
  if (reason === null || !reason.trim()) return;
  await run(() => eqsApi.rejectChangeApproval(props.changeId!, task.id, reason.trim()));
}

async function removeTask(task: Task): Promise<void> {
  const ok = await confirmAction({
    title: `删掉「${task.title.slice(0, 24)}」？`,
    message: '这一步就不再要求做这件事了。',
    danger: true,
    confirmLabel: '删掉',
  });
  if (!ok) return;
  await run(() => eqsApi.removeChangeTask(props.changeId!, task.id));
}

async function advance(): Promise<void> {
  const closing = atLastStep.value;
  await run(
    () => eqsApi.advanceChangeStep(props.changeId!),
    closing ? '这条变更已关闭' : `已推进到第 ${Math.min(currentStep.value + 1, steps.value.length)} 步`,
  );
}

/**
 * 这一步在界面上显示哪些任务。
 *
 * 第 1 步的任务区撤了（0826 Tony：那 4 条提示任务和表单重复）：
 * 表单任务的状态由表头「已提交 / 还没提交」表达，不再列一条灰任务。
 * 但**遗留的非表单任务照实显示**——启动清理漏掉的话，它们仍然挡推进，
 * 藏起来就是一道看不见的门禁，人对着一条说不清的报错没处下手。
 */
const visibleTasks = computed<Task[]>(() => {
  const step = active.value;
  if (!step) return [];
  if (step.order === 1) return step.tasks.filter((task) => task.source !== 'form');
  return step.tasks;
});

/* ==================================================================
   第 3 / 5 步的字段区（0826 建，0827 断点那几格随措施搬到第 5 步）

   通用字段区（变更标识/来源与关联/变更内容/台账与费用/实施与责任）按 Tony
   的要求删掉了。但两道门禁还要读其中几格：A/B 级进第 4 步要有「客户批准 /
   告知记录」，断点类措施标完成要有「实际断点」。这些字段搬进它们被决定的
   那一步里填，存的还是 ecm_changes.normalized 同一格（走通用更新接口，jsonb 合并）。
   ================================================================== */
/** 字段说明可以按本单情况变（第 3 步那格：客户发起和内部发起说的不是一句话） */
type StepFieldHelp = string | ((ctx: { level: string; fromCustomer: boolean }) => string);
interface StepField {
  key: string; label: string; type?: 'textarea' | 'select';
  options?: string[]; placeholder?: string; help?: StepFieldHelp;
}
/**
 * 一步可以有不止一块字段（第 5 步 0827 起就是两块：断点与标识 + 台账与费用），
 * 所以按块存：块自己带抬头、权限和保存按钮，保存**只送本块的格子**——
 * 一个按钮送两块的话，看不到费用的人一保存断点就会连带写费用键，必然 403。
 */
interface StepFieldBlock {
  /** 这一块允许贴图/传文件（客户批准那一格，0828） */
  attach?: boolean;
  key: string; title?: string; scopeNote?: string;
  /** 只有能看费用的人才显示这一块（后端读写两头另有同一道门） */
  costOnly?: boolean;
  /**
   * 只在这几个变更等级下显示（0827 Tony）。
   *
   * 「客户批准 / 告知记录」只对 A / B 级有意义——A 级要客户书面批准、B 级要告知记录，
   * C 级程序里就写着可留空。全库 24 条单子大部分是 C 级或还没定等级，
   * 对它们来说这一块从头到尾是噪音。
   *
   * **只藏不该填的，不藏挡路的**：后端那道门（assertChangeClientAuthorization）
   * 遇到 C 级和空等级本来就直接放行，所以藏起来不会出现「门还在、入口没了」。
   * 等级一改成 A/B，这一块自己就回来了。
   *
   * 客户发起的单子（0905 起后端那道门不再拦它）这一块**照样显示**、只是说明换成「不强制」：
   * 客户对方案另有确认的要有地方备案，藏掉就没处贴了。
   */
  levels?: readonly string[];
  /** 保存成功后那句话 */
  savedNote: string;
  fields: readonly StepField[];
}
const STEP_FIELD_BLOCKS: Readonly<Record<number, readonly StepFieldBlock[]>> = {
  3: [{
    key: 'authorization', savedNote: '客户批准 / 告知记录已更新。', levels: ['A', 'B'],
    // 0828 Tony:「客户批准/告知记录可以上传截图图片等文件支持」
    attach: true,
    fields: [{
      key: 'clientAuthorization', label: '客户批准 / 告知记录', type: 'textarea',
      /*
        0920：占位符原来最后一句是「截图可以直接 Ctrl+V 贴进来」——
        贴完门照样不开（门禁读的是这一格的文字），人自然以为系统坏了。
        贴图的能力留着（证据本来就该在系统里），但话要说清楚它是佐证不是答案。
      */
      placeholder: 'A 级：客户书面批准的出处（邮件主题+日期、EWO 号、存档位置）；'
        + 'B 级：告知记录（发给谁、什么时候、有无异议）。'
        + '截图 / 邮件 Ctrl+V 或拖进来存成佐证，但这一格得写字',
      /*
        0905 Tony：客户发起的变更，第 3 步不再硬性要求这一格（内部 / 供方发起的照旧）。
        说明要跟着门禁走——门已经不拦了还写着「进不了第 4 步」，人会白填、或者以为系统坏了。
        判据和后端 assertChangeClientAuthorization 同一条：来源是客户。
      */
      /*
        0920 Tony:「报错无法推进」。

        这句话原来写的是「A 级没登记这一格进不了第 4 步；B 级留告知记录；C 级可留空」——
        读起来像只有 A 级挡路，而后端 assertChangeClientAuthorization **B 级照样挡**
        （0904 定的：内部 / 供方发起的 A 和 B 都要登记）。
        于是 B 级的人填不填全凭自觉，一推进才撞上门，还以为系统坏了。
        说明和门禁必须说同一句话——这个坑上面那条注释刚警告过，这里自己又踩了。
      */
      help: ({ fromCustomer }) => (fromCustomer
        ? '客户发起的变更：这一格不强制。B 级不用再告知客户；A 级的书面授权以第 1 步贴的'
          + '「客户的变更要求（原文）」为准。客户对方案另有确认（邮件 / EWO）的，可以贴在这里备案。'
        : 'A 级和 B 级都要先登记这一格才进得了第 4 步（程序原文「流程退回」）：'
          + 'A 级写客户书面批准的出处，B 级写告知记录（发给谁、什么时候、有无异议）。'
          + 'C 级可以留空。只传附件不算——门禁看的是这一格的文字。'),
    }],
  }],
  /*
    第 5 步两块。

    ① 断点与标识：0827 Tony 把「库存清点与断点」那一组措施挪到第 5 步，
       这几格跟着搬——它们是那组措施的门禁（实际断点空着，ST/C08 标不了完成）。
       留在第 4 步的话，人在第 5 步点完成、被拦下、再退回第 4 步填，
       同一件事分在两屏，正是五步框架一直在收的那种毛病。
       程序 7.5 本来也写着「确认变更断点时间、产品标识版本」是这一步的事。
    ② 台账与费用（0826 Tony：费用仅本项目项目经理和管理员可见可填）：
       「确认变更费用/成本/进度」同属程序 7.5。备注不是钱，但它是台账管理员
       写的那一格，跟着块走。金额存文本不做算术（全库口径）。
  */
  5: [
    {
      key: 'breakpoint', title: '断点与标识', savedNote: '断点与标识已更新。',
      fields: [
        { key: 'partMarkBefore', label: '零件变更前标识', placeholder: '单件 P01/P02…，总成 A01/A02…' },
        { key: 'partMarkAfter', label: '零件变更后标识', placeholder: '变更后的新标识，写进作业 / 检验指导书' },
        {
          key: 'actualBreakpoint', label: '实际断点', placeholder: '真正切换的那一批 / 那一天 / 起始 SN',
          help: '断点做完回来填死——审计追溯查的就是这一格；空着时上面「库存清点与断点」的措施标不了完成。',
        },
        { key: 'oldStockDisposition', label: '旧件处置', type: 'select', options: ['用完为止', '返工使用', '报废', '退供应商'] },
      ],
    },
    {
      key: 'ledger', title: '台账与费用', costOnly: true, savedNote: '台账与费用已更新。',
      scopeNote: '仅本项目的项目经理和系统管理员可见、可填；金额存文本，不做算术。',
      fields: [
        { key: 'changeExpense', label: '变更费用', placeholder: '试制/模具/检具/工装/运输/报废等一次性费用合计' },
        { key: 'clientPayback', label: '客户支付', placeholder: '客户同意承担的金额（含治具费）' },
        { key: 'costImpact', label: '变更成本', placeholder: '产品单件成本的变化（涨写正、降写负）' },
        { key: 'priceImpact', label: '变更报价', placeholder: '产品单价的变化（客户同意增加的价格）' },
        { key: 'comments', label: '备注', placeholder: '台账备注' },
      ],
    },
  ],
};

/** 本单的变更等级（A/B/C），第 2 步评出来的；没评出来是空串 */
const changeLevel = computed(() => {
  const bag = (props.record?.normalized ?? {}) as Record<string, unknown>;
  return String(bag.changeLevel ?? '').trim().toUpperCase();
});

/**
 * 本单是不是客户发起的（第 1 步「变更来源」选了客户）。
 * 旧单的来源可能只存在拼音键 laiyuan 里（台账列也是两个键一起读），两个都看。
 * 和后端门禁 assertChangeClientAuthorization 认的是同一个判据。
 */
const fromCustomer = computed(() => {
  const bag = (props.record?.normalized ?? {}) as Record<string, unknown>;
  return String(bag.changeSource ?? bag.laiyuan ?? '').includes('客户');
});

/** 字段说明：写死的原样给，按本单情况变的现算 */
function fieldHelp(field: StepField): string {
  if (typeof field.help === 'function') {
    return field.help({ level: changeLevel.value, fromCustomer: fromCustomer.value });
  }
  return field.help ?? '';
}

/** 这一步显示哪几块：看不到费用的人不摆一排必然 403 的格子；不该填的等级也不摆 */
const activeBlocks = computed<StepFieldBlock[]>(() => {
  if (props.changeId === null || !active.value) return [];
  return (STEP_FIELD_BLOCKS[active.value.order] ?? [])
    .filter((block) => !block.costOnly || props.costVisible)
    .filter((block) => !block.levels || block.levels.includes(changeLevel.value));
});

const extras = reactive<Record<string, string>>({});
const extrasSaving = ref('');

/*
  初值按记录 id 灌一次：同一条单子上保存过的值以本地为准（父层的 record 只在
  状态变了时才刷新，拿它反灌会把刚存的值倒回旧值）；换了记录或重开抽屉才重灌。
*/
watch(() => props.changeId, () => { void loadAuthAttachments(); }, { immediate: true });

watch(() => props.record?.id, () => {
  const bag = (props.record?.normalized ?? {}) as Record<string, unknown>;
  for (const blocks of Object.values(STEP_FIELD_BLOCKS)) {
    for (const block of blocks) {
      for (const field of block.fields) extras[field.key] = String(bag[field.key] ?? '');
    }
  }
}, { immediate: true });

async function saveExtras(block: StepFieldBlock): Promise<void> {
  if (props.changeId === null || extrasSaving.value || !block.fields.length) return;
  extrasSaving.value = block.key;
  try {
    const normalized: Record<string, string> = {};
    for (const field of block.fields) normalized[field.key] = (extras[field.key] ?? '').trim();
    await eqsApi.updateModuleRecord('changes', 'changes', props.changeId, { normalized });
    notify('success', '已保存', block.savedNote);
    emit('changed');
  } catch (caught) {
    notify('error', '没保存上', errorMessage(caught));
  } finally {
    extrasSaving.value = '';
  }
}
</script>

<template>
  <section v-if="steps.length" class="change-steps">
    <header>
      <b>变更五步</b>
      <small v-if="isDraft">照《工程变更控制程序》7.1–7.5；下面第 1 步的表单填完提交，这条变更就立起来</small>
      <small v-else>照《工程变更控制程序》7.1–7.5；点一步看它的任务清单</small>
      <!-- 还没保存的单子推不了：推进要改单据状态，而单据还不存在 -->
      <!--
        0827 起最后一步上这颗按钮是「关闭这条变更」：进第 5 步不再等于关闭，
        关闭是站在第 5 步上的第二个动作（不然第 5 步那 5 条关闭任务永远没人管）。
        已经关了就不摆按钮——摆一颗点了报错的按钮不如不摆。
      -->
      <!-- 站在第 5 步时不摆这个：关闭有自己那张确认卡，两个按钮做同一件事最容易点错（0828） -->
      <!-- 0929 前这颗按钮连只读账号都摆着；现在只给能改这张单的人 -->
      <button
        v-if="!isDraft && !closed && !atLastStep && canEdit" type="button"
        class="button" :class="{ 'button-primary': currentReady }"
        :disabled="busy" :title="advanceHint" @click="advance"
      >{{ advanceLabel }}</button>
      <small v-else-if="!isDraft && closed" class="step-closed">这条变更已关闭</small>
    </header>

    <!--
      只能看的原因（0929）：不在这张单挂的项目组里、或者账号只读。按钮灰了得有人说为什么，
      不然人只会觉得系统坏了。点名给他的审批和任务照样能处理，后端那句话里说了。
    -->
    <p v-if="readonlyNote" class="step-readonly">
      <b>只能查看</b><span>{{ readonlyNote }}</span>
    </p>

    <!--
      当前这一步该干什么。原来是抽屉顶上 ChangeDrawerAssist 的「下一步」提示条，
      按单据状态取文案，第 1、2 步都在 submitted 下、说的是同一句话——
      那个组件已经删掉，文案按步搬到后端每步的 hint 上。
    -->
    <p v-if="currentHint" class="step-hint">
      <b>现在这一步</b><span>{{ currentHint }}</span>
    </p>

    <!--
      中途关闭的横幅（0828）。这条单没走完就关了，**当时还差什么必须一直挂着**——
      只把状态改成「关闭」，半年后没人知道它是走完的还是半路停的。
    -->
    <!--
      推不动的原因（0828 Tony:「希望点进去能够看到卡住的原因，而且能够明显一点的」）。
      原来这些话只在点了推进之后弹一下错，人看着一步「已完成 · 待推进」，
      不点一次不知道卡在哪。现在读的时候就摆出来，红框，摆在五步条正下方。
    -->
    <!--
      被退回了：理由摆在最显眼的地方（0920 Tony:「驳回理由也需要同步显示」）。
      原来界面上只有审批行末尾一个红色的「已驳回」三个字，
      为什么驳的只有驳的人自己知道——申请人得去翻站内信才看得到。
    -->
    <section v-if="rejection" class="reject-box">
      <header>
        <b>已退回给申请人</b>
        <small>
          <template v-if="rejection.level">第 {{ rejection.level }} 级</template>
          <template v-if="rejection.by"> · {{ rejection.by }}</template>
          <template v-if="rejection.at"> · {{ rejection.at }}</template>
        </small>
      </header>
      <p class="reject-reason">{{ rejection.reason }}</p>
      <footer>
        <span>按上面的理由改完之后重新提交，审批会从第 1 级重新走一遍。</span>
        <button
          v-if="rejection.canResubmit && canEdit"
          class="button button-primary" type="button"
          :disabled="resubmitting"
          @click="resubmit"
        >{{ resubmitting ? '正在提交…' : '改完了，重新提交' }}</button>
      </footer>
    </section>

    <section v-if="blockers.length && !closed" class="blocked-box">
      <header>
        <b>现在推不动</b>
        <small>第 {{ currentStep }} 步 · {{ stepName(currentStep) }} —— 下面这些先处理掉</small>
      </header>
      <ul>
        <li v-for="(reason, index) in blockers" :key="index">{{ reason }}</li>
      </ul>
    </section>

    <section v-if="earlyClose" class="early-banner">
      <header>
        <b>这是一条中途关闭的变更</b>
        <small>
          {{ earlyClose.by || '—' }} 于 {{ (earlyClose.at || '').slice(0, 10) }} 在
          第 {{ earlyClose.atStep }} 步关闭 · {{ stepName(earlyClose.atStep ?? 0) }}
        </small>
      </header>
      <p class="early-reason">原因：{{ earlyClose.reason }}</p>
      <details v-if="earlyClose.unfinished?.length">
        <summary>关闭时还有 {{ earlyClose.unfinished.length }} 项没做完</summary>
        <ul>
          <li v-for="(item, index) in earlyClose.unfinished" :key="index">
            <span class="early-step">第{{ item.step ?? '—' }}步</span>
            <b>{{ item.code ? `${item.code} ` : '' }}{{ item.title }}</b>
            <em v-if="item.owner">{{ item.owner }}</em>
          </li>
        </ul>
      </details>
    </section>

    <ol>
      <li v-for="step in steps" :key="step.code" :class="[stateOf(step), { blocked: blockers.length && step.order === currentStep }, { open: openStep === step.order }]">
        <button type="button" @click="openStep = openStep === step.order ? null : step.order">
          <i>{{ step.order }}</i>
          <span>
            <b>{{ step.name }}</b>
            <!-- 还没保存的单子谈不上「铺没铺任务」，说那句是误导 -->
            <em v-if="isDraft">{{ step.order === 1 ? '正在填' : '保存后进行' }}</em>
            <!--
              做完了但还没推进过去：亮起来，同时说清还差一下（0827 Tony
              「第四步完成也没有点亮」）。不写成「已完成」——这一步的
              step_advanced 审计还没有，说完成就是替人点了推进。
            -->
            <em v-else-if="step.total">
              {{ step.done }}/{{ step.total }} 已完成{{ stateOf(step) === 'ready' ? ' · 待推进' : '' }}
              <template v-if="step.voided"> · 不适用 {{ step.voided }}</template>
            </em>
            <!--
              这一步的措施**全部**设成了不适用：清单是生成过的，只是这次都不用做。
              作废的不进分子分母，所以 total 是 0——照 total 说「还没生成」是句假话
              （线上 ECN-EEE-001 的第 5 步正好 6 条全不适用）。
            -->
            <em v-else-if="step.voided">
              {{ step.voided }} 条已不适用{{ stateOf(step) === 'ready' ? ' · 待推进' : '' }}
            </em>
            <!--
              和第 3 步「到这一步自动铺审批链」一个说法：还没走到的步别说成
              「还没生成」——那读着像缺了东西，而它到时候会自己出来（0827）。
            -->
            <em v-else-if="step.fromChecklist && step.order > currentStep">到这一步自动生成清单</em>
            <em v-else-if="step.fromChecklist">执行清单还没生成</em>
            <!-- 第 3 步不是「没人铺」：走到这一步会自动按路由铺链并派人，别让人白等 -->
            <em v-else-if="step.order === 3">到这一步自动铺审批链</em>
            <em v-else>还没铺任务</em>
          </span>
        </button>
      </li>
    </ol>

    <div v-if="active" class="change-step-detail">
      <p class="detail-head">
        <b>第 {{ active.order }} 步 · {{ active.name }}</b>
        <span>{{ active.owner }}</span>
      </p>
      <p class="detail-exit"><b>做完的标准：</b>{{ active.exit }}</p>

      <!--
        本单的审批链（0825 需求 4）：第 3 步按「量产前后 × 变更等级」四场景铺任务，
        铺出来之前人得先看得见这单要走哪条链。自动派到具体人这轮不做
        （「直属部门经理」要查组织层级，钉钉部门主管数据没核实过），照实说。
      -->
      <template v-if="routeLine">
        <p class="detail-route"><b>本单审批路径：</b>{{ routeLine }}</p>
        <!--
          阶段/等级改过、链却换不了（下面已经有人批了）。不吭声的话，
          上面这行路径和下面的任务是两条不同的链，人照哪条做都不对（0827）。
        -->
        <p v-if="active?.routeStale" class="detail-route-stale">
          ⚠ 这一单的发生阶段 / 变更等级改过，<b>下面的审批任务还是改之前那条链</b>——
          已经有人批过，系统不敢自动删。要按新路径走，请把下面这几条审批任务删掉，
          再点「一键铺标准任务」重铺；已经批过的那几级要重新批一次。
        </p>
        <!-- 0825 起自动派人（唯一命中才派）、0826 起自动铺+站内提醒；老文案说「没自动指派」是过时的 -->
        <p class="detail-route-note">
          走到第 3 步会按这条链自动铺出审批任务、自动派人（唯一命中才派）并给被派到的人发站内提醒；
          没派上的会点名原因，逐条手工指定。
          <b>标「会签」的那一级几个人同时收到提醒</b>，谁先批谁先勾，这一级全批完才轮到下一级。
        </p>
      </template>

      <!--
        第 1、2 步有正式表单（0823 Tony 手绘稿）。
        表单提交 = 勾完这一步的表单任务，所以提交完要把清单重读一遍，
        不然那条「填写并提交…」还显示成没勾。
      -->
      <ChangeStepForm
        v-if="props.changeId !== null && (active.order === 1 || active.order === 2)"
        :change-id="props.changeId" :step="active.order"
        :project-options="props.projectOptions" :people="props.people"
        :readonly="!canEdit"
        @submitted="reload"
      />

      <!--
        第 4、5 步的「任务」就是执行清单（ecm_change_actions）本身。
        原来这两步另铺 5 条粗任务，是 41 条动作的低分辨率重复；
        抽屉底下那份独立的清单区块也撤了，措施的增删改状态一律在这一份上做。
        标题头不重复写（上面已经有「第 N 步 · …」），但「共 N 条」和「＋ 添加」留着。

        editable 仍按账号给、不跟 canEdit（0929）：措施走通用子记录写入，门在
        modules/record-write-access——按项目收，但措施责任人本人能改自己那一条。
        跟了 canEdit，会把组外的措施责任人连自己那条都挡在外面。
      -->
      <RecordChildList
        v-if="active.fromChecklist && props.changeId !== null"
        module="changes"
        resource="actions"
        :parent-id="props.changeId"
        title="执行清单"
        hide-title
        :empty-hint="checklistEmptyHint"
        :extra-fields="ACTION_EXTRA_FIELDS"
        :code-groups="active.groups"
        :foreign-codes="active.foreignCodes"
        :editable="props.writable"
        :before-advance="props.beforeChildAction"
        with-attachments
        void-label="不适用"
        @changed="reload"
      >
        <!--
          库存清点表挂在它该挂的那几条措施底下（0826 Tony）。
          原来这张表被拆在 8 条措施里各写各的，同一个数记好几遍还对不上；
          现在一张表六行，谁填哪一行有主，处置的人直接看数。
          0827 起这几条措施横跨两步（EX03/EX12 在第 4 步、ST01–ST06 在第 5 步），
          所以不再按步判——认措施代号就行，挂在哪一步都摆得出这张表。
        -->
        <template #item-panel="{ item }">
          <ChangeInventoryTable
            v-if="props.changeId !== null && inventoryFor(item.code).length"
            :change-id="props.changeId"
            :rows="inventoryFor(item.code)"
            :mode="inventoryMode(item.code)"
            :owner-name="(item.owner_name ?? item.ownerName) as string | null"
            :editable="canEdit"
            @saved="loadInventory"
          />
        </template>
      </RecordChildList>

      <!--
        第 3 / 5 步的字段区：通用字段区删掉后，门禁要读的几格搬进它们被决定的
        那一步。第 3 步登记客户批准（A/B 级进第 4 步的门禁）；第 5 步两块——
        断点与标识（0827 跟着「库存清点与断点」那组措施搬过来，是它们的门禁）
        和台账与费用（权限块，看不到费用的人整块不显示）。
      -->
      <div v-if="activeBlocks.length" class="step-forms">
      <div v-for="block in activeBlocks" :key="block.key" class="step-fields">
        <b v-if="block.title">{{ block.title }}</b>
        <!-- 台账费用是权限块：让填的人知道别人看不到，别当成公共字段到处引用 -->
        <small v-if="block.scopeNote" class="step-fields-scope">{{ block.scopeNote }}</small>
        <div class="step-fields-grid">
          <label
            v-for="field in block.fields" :key="field.key"
            class="step-field" :class="{ wide: field.type === 'textarea' }"
          >
            <span>{{ field.label }}</span>
            <textarea
              v-if="field.type === 'textarea'" v-model="extras[field.key]" rows="3"
              :placeholder="field.placeholder" :disabled="!canEdit"
              @paste="block.attach ? onAuthPaste($event) : undefined"
              @drop="block.attach ? onAuthDrop($event) : undefined"
              @dragover="block.attach ? $event.preventDefault() : undefined"
            ></textarea>
            <select v-else-if="field.type === 'select'" v-model="extras[field.key]" :disabled="!canEdit">
              <option value="">请选择</option>
              <option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
            </select>
            <input
              v-else v-model="extras[field.key]" type="text"
              :placeholder="field.placeholder" :disabled="!canEdit"
            >
            <small v-if="fieldHelp(field)">{{ fieldHelp(field) }}</small>
          </label>
        </div>
        <!--
          传证据（0828）：A 级的「客户书面批准」本来就是一封邮件截图或一个 PDF。

          0920 Tony:「拖拽进去的文件直接去下面业务附件中……而且看不到」——
          传上去的文件现在**就列在这一格下面**（打了归属标记，不再混进底下那一长串），
          传完立刻看得见、传错了能撤下。
        -->
        <div v-if="block.attach" class="block-attach">
          <ul v-if="authAttachments.length" class="auth-attach-list">
            <li v-for="file in authAttachments" :key="file.id">
              <a :href="authAttachmentUrl(file.id)" target="_blank" rel="noopener">{{ file.fileName }}</a>
              <small>{{ Math.max(1, Math.round(file.sizeBytes / 1024)) }} KB<template v-if="file.uploadedBy"> · {{ file.uploadedBy }}</template></small>
              <button
                v-if="canEdit"
                class="inline-button auth-attach-remove" type="button"
                :disabled="file.canRemove === false || authRemoving === file.id"
                :title="file.removeBlockedReason || '把这份材料从这条变更上撤下来（不会真的删掉）'"
                @click="removeAuthAttachment(file)"
              >{{ authRemoving === file.id ? '撤下中…' : '撤下' }}</button>
            </li>
          </ul>
          <template v-if="canEdit">
            <input ref="attachInput" type="file" multiple @change="onAuthPick">
            <small>
              <template v-if="attachBusy">正在存…</template>
              <template v-else>
                截图可以直接 Ctrl+V 贴进上面的框，也可以拖文件进来或在这里选——都列在这一格下面。
                <b>文件是佐证，上面那格文字还是要写</b>：门禁看的是文字，不是附件。
              </template>
            </small>
          </template>
        </div>
        <button
          v-if="canEdit" class="button" type="button"
          :disabled="!!extrasSaving" @click="saveExtras(block)"
        >{{ extrasSaving === block.key ? '保存中…' : '保存' }}</button>
      </div>
      </div>

      <!--
        关闭确认（0828 Tony:「我需要变更管理者确认所有任务已完成，资料完整
        关闭变更流程」）。

        原来关闭只是右上角那个通用「推进」按钮换了行字，和「推进到第 4 步」
        长得一模一样——一个终局动作混在流程按钮里，谁点了都不知道自己在结案。
        现在第 5 步末尾单独一张卡：先把三件事摆出来（任务、清单、资料），
        再要变更管理者**逐条勾**，最后才是那个绿按钮。
      -->
      <section v-if="onClosePanel" class="close-card" :class="{ 'not-yet': !atLastStep }">
        <header>
          <b>确认关闭这条变更</b>
          <small v-if="atLastStep">
            由变更管理者{{ closeOwner ? `（${closeOwner}）` : '' }}确认；关闭后台账状态变成「关闭」。
          </small>
          <!-- 还没走到第 5 步：说清现在在哪、下一步该谁动，别让人以为按钮丢了 -->
          <small v-else>
            这条变更现在在<b>第 {{ currentStep }} 步 · {{ stepName(currentStep) }}</b>，
            还没走到闭环关闭。{{ pendingHint }}
          </small>
        </header>

        <ul class="close-checks">
          <li :class="{ ok: closeReady.tasks }">
            <span>{{ closeReady.tasks ? '✓' : '✕' }}</span>
            <div>
              <b>第 5 步任务全部完成</b>
              <em>{{ closeReady.taskDone }}/{{ closeReady.taskTotal }} 已完成</em>
            </div>
          </li>
          <li :class="{ ok: closeReady.checklist }">
            <span>{{ closeReady.checklist ? '✓' : '✕' }}</span>
            <div>
              <b>执行清单都有交代</b>
              <em>{{ active.done }}/{{ active.total }} 已完成{{ active.voided ? ` · 不适用 ${active.voided}` : '' }}</em>
            </div>
          </li>
          <li :class="{ ok: closeReady.files }">
            <span>{{ closeReady.files ? '✓' : '!' }}</span>
            <div>
              <b>变更资料已上传</b>
              <em v-if="closeReady.files">已挂 {{ props.attachmentCount ?? 0 }} 份</em>
              <em v-else>这条变更下面一份资料都没有——验证报告、客户批复、过程记录传上来再关。</em>
            </div>
          </li>
        </ul>

        <!-- 勾选是「确认」不是「门禁」：门禁在后端，这两下是让人真的看一眼再点 -->
        <template v-if="atLastStep">
        <label class="close-vow">
          <input v-model="vowTasks" type="checkbox" :disabled="!canEdit">
          <span>我确认这条变更的所有任务都已完成</span>
        </label>
        <label class="close-vow">
          <input v-model="vowFiles" type="checkbox" :disabled="!canEdit">
          <span>我确认变更资料完整（验证报告、客户批复、过程记录都已上传）</span>
        </label>

        <div class="close-foot">
          <small v-if="!closeReady.all" class="close-block">
            还差：{{ closeBlockers.join('、') }}。
          </small>
          <button
            class="button button-primary close-go" type="button"
            :disabled="!canEdit || busy || !vowTasks || !vowFiles"
            @click="advance"
          >{{ busy ? '关闭中…' : '确认关闭变更' }}</button>
        </div>
        </template>

        <!--
          中途关闭：不走门禁，所以它长得就该和上面那个绿按钮不一样——
          一条不起眼的次要入口，点开才要写原因。
          没走完也能关是**事实需要**（客户撤销、方案推翻），但不能顺手点掉。
        -->
        <div v-if="canEdit && !closed" class="early-close">
          <template v-if="!earlyOpen">
            <!--
              两个「不往下走了」的出口并排（0828）。它们不是一回事：
              中途关闭 = 做过一部分，停下来关档，台账写「中途关闭」；
              作废     = 这条申请本身不成立，撤掉，台账写「终止」。
              各写一句，人才知道该点哪个。
            -->
            <div class="stop-row">
              <div>
                <button class="inline-button" type="button" @click="earlyOpen = true">中途关闭 →</button>
                <small>做过一部分，不往下做了。会把没做完的记进单据，台账标「中途关闭」。</small>
              </div>
              <div>
                <button class="inline-button inline-button-danger" type="button" @click="emit('void')">作废 →</button>
                <small>这条申请本身不成立，整条撤掉，台账标「终止」。已执行过的动作要按程序 10.4 发反向变更恢复原状。</small>
              </div>
            </div>
          </template>
          <template v-else>
            <label>
              <span>为什么中途关闭（会记进单据，台账上能查到）</span>
              <textarea
                v-model="earlyReason" rows="2"
                placeholder="例：客户撤销该造型方案，本单不再执行"
              ></textarea>
            </label>
            <p class="early-warn">
              还没做完的
              <b>{{ closeReady.taskTotal - closeReady.taskDone + Math.max(0, (active?.total ?? 0) - (active?.done ?? 0)) }}</b>
              项会原样记进单据，标明是中途关闭。
            </p>
            <div class="early-actions">
              <button class="button" type="button" @click="earlyOpen = false">取消</button>
              <button
                class="button button-danger" type="button"
                :disabled="busy || earlyReason.trim().length < 5"
                @click="closeEarly"
              >{{ busy ? '关闭中…' : '确认中途关闭' }}</button>
            </div>
            <small v-if="earlyReason.trim().length < 5" class="early-hint">原因至少写 5 个字。</small>
          </template>
        </div>
      </section>

      <!--
        不在清单口径内的老措施（0827）：「其他措施」那一组撤掉了，
        但条数要照实说一句——藏起来不说才是这套系统真正吃过亏的事。
      -->
      <p v-if="active.fromChecklist && active.legacy" class="legacy-note">
        另有 {{ active.legacy }} 条旧 EQS 迁来的老措施不在这份清单的口径里
        （没有动作编号，不计进度、不挡推进）。数据还在库里，导出和审计都查得到。
      </p>

      <!--
        0824 之前已经在第 4/5 步铺过 ecm_change_tasks 的老单子，那些行还在库里。
        照常显示、推进照旧要求勾完——静默消失比多显示一块难发现得多。
      -->
      <p v-if="active.fromChecklist && active.tasks.length" class="legacy-note">
        旧版一键铺的任务：0824 之前铺的粗清单，推进这一步照旧要求勾完；新变更不再铺这一份。
      </p>

      <ul v-if="visibleTasks.length" class="task-list">
        <!--
          source='form' 是这一步的表单任务：勾选框和「删」都对它关闭。
          能手工勾就等于门禁退化成一个复选框，能删就等于必填项可以绕过——
          它只有一条完成路径：把下面那张表交上来。
        -->
        <li v-for="task in visibleTasks" :key="task.id" :class="{ done: !!task.doneAt }">
          <!--
            审批行不给勾选框：它有两个结果（通过 / 驳回），
            而且只有轮到的那个人能出结论（0827 Tony）。
          -->
          <label v-if="!isApproval(task)">
            <input
              type="checkbox" :checked="!!task.doneAt"
              :disabled="busy || task.source === 'form' || !canActOn(task)" @change="toggle(task)"
            >
            <span>{{ task.title }}</span>
          </label>
          <span v-else class="task-approval-title">{{ task.title }}</span>
          <small>
            <template v-if="task.source === 'form'">表单填完自动勾上</template>
            <template v-else-if="task.ownerName">{{ task.ownerName }}</template>
            <template v-if="task.dueAt"> · 期限 {{ task.dueAt }}</template>
            <template v-if="task.doneAt"> · 由 {{ task.doneBy || '—' }} {{ task.conclusion === 'rejected' ? '驳回' : '批准' }}</template>
          </small>
          <!--
            没轮到的那几级不摆按钮（0828 Tony:「第三步也要增加类似的逐级审批」）。
            后端已经拦住跳级批，但界面上照旧摆着「通过 / 驳回」——
            点了才弹错，等于把「轮到没轮到」这件事推给人去试。
            现在直接写清在等哪一级，按钮只给当前该批的那一级。
          -->
          <!--
            整条链已经被驳回：后面几级不摆按钮（0920 Tony:「驳回了还会流到下一人进行审批」）。
            后端已经拦住了，但界面上照旧摆着「通过 / 驳回」——
            点了才弹错，等于把「这条还要不要批」推给人去试。
          -->
          <span v-if="isApproval(task) && !task.doneAt && rejection" class="task-waiting">
            上一级已退回，这一级不用批了
          </span>
          <span v-else-if="isApproval(task) && !task.doneAt && !levelReached(task)" class="task-waiting">
            等第 {{ waitingLevel }} 级批完
          </span>
          <!--
            按钮只给能批这一级的人（0929 起看后端的 canAct）：本人、管理员；没派人的那一级
            项目组里的人都能批。不在项目组的审批人（量产后会签的三位经理常是这样）照样看得到自己那一级的按钮。
          -->
          <span v-else-if="isApproval(task) && !task.doneAt && canActOn(task)" class="task-approval-acts">
            <button
              class="inline-button" type="button"
              :disabled="busy"
              @click="approve(task)"
            >通过</button>
            <button
              class="inline-button task-danger" type="button"
              :disabled="busy"
              title="驳回后整条审批链到此为止，单子退回给申请人改"
              @click="reject(task)"
            >驳回</button>
          </span>
          <span v-else-if="isApproval(task) && !task.doneAt" class="task-waiting">{{ approvalWaiting(task) }}</span>
          <span v-else-if="isApproval(task) && task.conclusion === 'rejected'" class="task-rejected">已驳回</span>
          <button
            v-if="task.source !== 'form' && canEdit" class="inline-button" type="button"
            :disabled="busy" @click="removeTask(task)"
          >删</button>
        </li>
      </ul>
      <!-- 新建态说的是另一回事：任务要等单子存在了才铺得出来，不是「还没铺」 -->
      <p v-if="isDraft" class="task-empty">
        <template v-if="active.order === 1">
          这一步就是下面这张《变更发起申请》表单——填完提交，这条变更就立起来。
        </template>
        <template v-else>
          这一步等第 1 步提交之后才开始——那时这里会出现它的任务清单和表单。
        </template>
      </p>
      <!-- 第 1 步不再有任务区（0826）：表单本身就是这一步的全部必填项 -->
      <p v-else-if="!visibleTasks.length && active.order === 3" class="task-empty">
        审批任务会在推进到第 3 步时按上面的路径<b>自动铺出并派人</b>（被派到的人收到站内提醒）；
        要提前铺也可以点下面的「一键铺标准任务」。
      </p>
      <p v-else-if="!visibleTasks.length && !active.fromChecklist && active.order !== 1" class="task-empty">
        这一步还没有任务。可以「一键铺标准任务」把程序文件里点到的事项铺出来，再按实际情况增删。
      </p>

      <!-- 新建态一律不给写入类操作；第 1 步的任务区撤了（0826），铺任务/加任务都不给 -->
      <!--
        0929：这一行按「这张单能不能改」（canEdit）给，只有催办例外——
        催办只发提醒不改数据，Tony 0929 定的是「能看就能催」，不按项目收。
        不能改的人在非清单步上这一行什么都没有，整行不摆。
      -->
      <div
        v-if="props.writable && !isDraft && active.order !== 1 && (canEdit || active.fromChecklist)"
        class="task-add"
      >
        <!--
          清单步的「铺任务」就是生成执行清单本身（0825 起是 44 条按阶段的模板），
          按第 2 步勾的影响范围裁；催办也挪到这里（原来在抽屉顶上）。
          第 5 步同样给这两个按钮：0827 库存断点组挪过去以后，
          人在第 5 步发现清单还没生成、却被打发回第 4 步点按钮，是白绕一圈。
        -->
        <template v-if="active.fromChecklist">
          <button v-if="canEdit" class="inline-button" type="button" :disabled="generating" @click="generate">
            {{ generating ? '生成中…' : '✨ 按影响范围生成执行清单' }}
          </button>
          <button class="inline-button" type="button" :disabled="reminding" @click="remind">
            {{ reminding ? '催办中…' : '催办未完成措施' }}
          </button>
        </template>
        <!--
          铺不铺得出标准任务由后端说了算（seedable）：第 1 步的必填项是表单本身、
          第 4 步的任务就是执行清单，这两步铺出来会是 0 条，按钮不该摆在那里。
          第 5 步两样都有——程序 7.5 的 5 条关闭任务照旧一键铺，清单另生成。
        -->
        <button
          v-if="canEdit && active.seedable !== false && !active.fromChecklist"
          class="inline-button" type="button" :disabled="busy" @click="seed(active.order)"
        >✨ 一键铺标准任务</button>
        <button
          v-else-if="canEdit && active.seedable"
          class="inline-button" type="button" :disabled="busy" @click="seed(active.order)"
        >✨ 一键铺这一步的关闭任务</button>

        <!-- 清单步临时加一条走上面清单的「＋ 添加」，不再往 ecm_change_tasks 里加新的 -->
        <template v-if="!active.fromChecklist && canEdit">
          <input v-model="newTitle" type="text" placeholder="加一条任务…" @keydown.enter="addTask(active.order)">
          <input v-model="newOwner" list="change-task-people" type="text" placeholder="责任人">
          <datalist id="change-task-people">
            <option v-for="person in props.people ?? []" :key="person" :value="person" />
          </datalist>
          <input v-model="newDue" type="date" aria-label="完成期限">
          <button class="button" type="button" :disabled="busy || !newTitle.trim()" @click="addTask(active.order)">
            加任务
          </button>
        </template>
        <!--
          「＋ 添加」加出来的措施没有动作编号，不在清单口径内，
          所以不会出现在上面任何一组里——不说清楚，人加完会以为丢了（0827）。
        -->
        <small v-else-if="active.fromChecklist && canEdit" class="task-add-note">
          临时加一条措施用上面清单里的「＋ 添加」；新加的没有动作编号，
          不进上面的分组，也不计进度。
        </small>
      </div>
    </div>

    <!--
      新建态的第 1 步表单（0826：通用建单表单撤了，新建屏和编辑屏同一张表）。
      常驻挂载、v-show 控显隐：点去看第 2–5 步的「做完的标准」再回来，
      填了一半的内容还在——用 v-if 会随切换销毁重建，把人打的字无声扔掉。
    -->
    <div v-if="isDraft" v-show="openStep === 1 || openStep === null" class="change-step-draft">
      <ChangeStepForm
        :change-id="null" :step="1"
        :project-options="props.projectOptions" :people="props.people" :creating="props.creating"
        @create="(payload) => emit('create', payload)"
      />
    </div>
  </section>
</template>

<style scoped>
.change-steps { margin: 12px 0; padding: 12px 14px; border: 1px solid var(--line); border-radius: 12px; background: var(--white); }
.change-steps > header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
.change-steps > header small { flex: 1 1 200px; color: var(--muted); font-size: 11px; }
/* 「下一步」提示条从抽屉顶上搬过来，样式照搬 ChangeDrawerAssist 那条蓝边框 */
.step-hint {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px;
  margin: 0 0 10px; padding: 8px 11px;
  border-left: 3px solid var(--blue); background: rgba(0, 117, 174, .06);
  color: var(--ink); font-size: 12px; line-height: 1.7;
}
.step-hint > b { color: var(--blue); font-size: 12px; white-space: nowrap; }
.step-hint > span { flex: 1; min-width: 220px; }
/* 只能查看（0929）：和上面的提示条同一个排法，换成琥珀色——是提醒，不是报错 */
.step-readonly {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px;
  margin: 0 0 10px; padding: 8px 11px;
  border-left: 3px solid #d97706; background: #fffbeb;
  color: var(--ink); font-size: 12px; line-height: 1.7;
}
.step-readonly > b { color: #b45309; font-size: 12px; white-space: nowrap; }
.step-readonly > span { flex: 1; min-width: 220px; }
/*
  五步一行摆五格（0828 Tony:「整个变更界面的框有大有小，错乱分布」）。
  原来是 auto-fit minmax(150px)：窗口宽度一变，第 5 格就掉到下一行独占一整行，
  看着像多出来一个东西。五步是固定的五步，就按五列排；窄屏才折成两行。
*/
.change-steps > ol { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin: 0; padding: 0; list-style: none; }
@media (max-width: 900px) { .change-steps > ol { grid-template-columns: repeat(2, 1fr); } }
.change-steps > ol > li > button {
  display: flex; align-items: flex-start; gap: 9px; width: 100%; height: 100%; padding: 10px;
  border: 1px solid var(--line); border-radius: 10px; background: transparent; text-align: left; cursor: pointer;
}
.change-steps i {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; background: var(--line);
  font-style: normal; font-size: 12px; font-weight: 700;
}
.change-steps span { display: grid; gap: 2px; min-width: 0; }
.change-steps b { font-size: 13px; }
.change-steps em { font-style: normal; color: var(--muted); font-size: 11px; }
.change-steps > ol > li.done > button { border-color: rgba(46, 158, 107, .45); background: rgba(46, 158, 107, .06); }
.change-steps > ol > li.done i { background: #2e9e6b; color: #fff; }
/*
  ready = 活干完了、还没推进过去（0827）。绿边框但圆圈是空心的：
  一眼看得出亮了，也一眼区分得开「走过了」那种实心绿。
*/
.change-steps > ol > li.ready > button { border-color: rgba(46, 158, 107, .45); background: transparent; }
.change-steps > ol > li.ready i { background: transparent; box-shadow: inset 0 0 0 2px #2e9e6b; color: #2e9e6b; }
.change-steps > ol > li.ready em { color: #2e9e6b; }
.change-steps > ol > li.current > button { border-color: var(--blue); background: rgba(18, 109, 166, .07); }
.change-steps > ol > li.current i { background: var(--blue); color: #fff; }
.change-steps > ol > li.open > button { box-shadow: 0 0 0 2px rgba(18, 109, 166, .18); }

.change-step-detail { margin-top: 10px; padding: 10px 12px; border-radius: 10px; background: var(--surface-2, #f7f9fc); font-size: 12px; }
.detail-head { display: flex; align-items: baseline; gap: 10px; margin: 0 0 4px; }
.detail-head span { color: var(--muted); font-size: 11px; }
.detail-exit { margin: 0 0 8px; color: var(--ink); }
/* 第 3 步的审批路径（0825）：跟在「做完的标准」下面，一行链路 + 一行小字说明 */
.detail-route { margin: 0 0 2px; color: var(--ink); }
.detail-route-stale {
  margin: 4px 0 0; padding: 6px 10px;
  border-left: 3px solid var(--risk); background: #FDF1EF;
  color: var(--ink); font-size: 12px; line-height: 1.8;
}
.detail-route-note { margin: 0 0 8px; color: var(--muted); font-size: 11px; }
/* 遗留任务和上面的执行清单不是一回事，得看得出来是两块 */
.legacy-note { margin: 12px 0 5px; color: var(--muted); font-size: 12px; line-height: 1.6; }
.task-list { display: grid; gap: 5px; margin: 0 0 8px; padding: 0; list-style: none; }
/* 审批行：标题不再是勾选框的 label，得自己占位 */
.task-approval-title { flex: 1 1 auto; min-width: 0; }
.task-approval-acts { display: inline-flex; gap: 6px; }
.task-approval-acts .task-danger { color: var(--risk); border-color: var(--risk); }
.task-waiting { color: var(--muted); font-size: 11px; white-space: nowrap; }
.task-rejected { color: var(--risk); font-weight: 800; font-size: 12px; }

/* 被退回的横幅：比「推不动」更要紧，所以摆在它上面、用更重的底色 */
.reject-box {
  margin: 0 0 12px;
  padding: 12px 14px;
  border: 1px solid #fecaca;
  border-radius: 12px;
  background: #fef2f2;
}
.reject-box header { display: flex; align-items: baseline; gap: 8px; }
.reject-box header b { color: #b91c1c; font-size: 14px; }
.reject-box header small { color: #9f1239; font-size: 12px; }
.reject-reason {
  margin: 8px 0 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  line-height: 1.7;
  color: #7f1d1d;
  white-space: pre-wrap;
}
.reject-box footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #9f1239;
}
.task-list li { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border: 1px solid var(--line); border-radius: 8px; background: var(--white); }
.task-list label { display: flex; align-items: flex-start; gap: 7px; flex: 1 1 auto; min-width: 0; cursor: pointer; }
.task-list li.done span { color: var(--muted); text-decoration: line-through; }
.task-list small { flex: 0 0 auto; color: var(--muted); font-size: 11px; }
.task-empty { margin: 0 0 8px; color: var(--muted); }
.task-add { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.task-add input[type="text"] { flex: 1 1 160px; min-width: 120px; }
.task-add-note { color: var(--muted); font-size: 11px; }
/* 第 3/4 步的字段区（0826）：门禁要读的几格搬进对应步骤，小格布局 */
/*
  第 5 步两块字段（断点与标识 / 台账与费用）收进一个外框：
  0828 Tony「整个布局也很乱」——两块各带一个「保存这几格」按钮平铺在一起，
  看不出它们是同一步下面的东西。
*/
.step-forms { display: grid; gap: 10px; margin: 12px 0; padding: 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--surface-soft, #f8fafc); }
.step-forms .step-fields { margin: 0; }

/* 关闭确认卡：终局动作要长得和流程按钮不一样 */
.close-card { margin: 16px 0 4px; padding: 14px 16px; border: 1px solid #a7f3d0; border-radius: 10px; background: linear-gradient(180deg, #f0fdf4, var(--white)); }
/* 还没轮到关闭：卡还在（让人知道它存在），但收成灰的，不摆勾选和按钮 */
.close-card.not-yet { border-color: var(--line); background: var(--surface-soft, #f8fafc); }
.close-card.not-yet > header b { color: var(--muted); }
.close-card > header { display: flex; flex-direction: column; gap: 3px; margin-bottom: 10px; }
.close-card > header b { font-size: 14px; }
.close-card > header small { color: var(--muted); font-size: 12px; }
.close-checks { list-style: none; margin: 0 0 12px; padding: 0; display: grid; gap: 8px; }
.close-checks li { display: flex; gap: 10px; align-items: flex-start; padding: 8px 10px; border-radius: 6px; background: #fff; border: 1px solid var(--line); }
.close-checks li > span { width: 18px; text-align: center; font-weight: 700; color: #b45309; }
.close-checks li.ok > span { color: #059669; }
.close-checks b { display: block; font-size: 13px; }
.close-checks em { display: block; margin-top: 2px; color: var(--muted); font-size: 12px; font-style: normal; line-height: 1.6; }
.close-vow { display: flex; gap: 8px; align-items: flex-start; margin-bottom: 6px; font-size: 13px; cursor: pointer; }
.close-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 10px; }
.close-block { color: #b45309; font-size: 12px; }
.close-go { margin-left: auto; }

/* 中途关闭：次要入口，长得不能像上面那个绿按钮 */
.early-close { margin-top: 12px; padding-top: 10px; border-top: 1px dashed var(--line); }
.stop-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
.stop-row small { display: block; margin-top: 4px; color: var(--muted); font-size: 11px; line-height: 1.6; }
.early-close label { display: block; font-size: 12px; }
.early-close label > span { display: block; margin-bottom: 4px; color: var(--muted); font-size: 11px; }
.early-close textarea { box-sizing: border-box; width: 100%; min-height: 54px; padding: 7px 9px; border: 1px solid var(--line); border-radius: 6px; font-size: 13px; font-family: inherit; }
.early-warn { margin: 8px 0 0; color: #b45309; font-size: 12px; }
.early-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.early-hint { display: block; margin-top: 4px; color: var(--muted); font-size: 11px; text-align: right; }
.button-danger { border-color: #fca5a5; background: #fee2e2; color: #991b1b; }

/* 中途关闭的横幅：这件事要一直挂着，不能只把状态改成「关闭」 */
/* 卡住的那一步在五步条里也标红，不然人得在下面的红框和上面的格子之间来回找 */
.change-steps > ol > li.blocked > button { border-color: #fca5a5; background: #fef2f2; }
.change-steps > ol > li.blocked i { background: #fca5a5; color: #7f1d1d; }

/* 推不动的红框：这是要人动手的事，颜色就该跳出来 */
.blocked-box { margin: 12px 0; padding: 12px 14px; border: 2px solid #fca5a5; border-radius: 12px; background: #fef2f2; }
.blocked-box > header { display: flex; flex-direction: column; gap: 2px; margin-bottom: 8px; }
.blocked-box > header b { color: #991b1b; font-size: 13px; }
.blocked-box > header small { color: #b91c1c; font-size: 11px; }
.blocked-box ul { margin: 0; padding-left: 18px; display: grid; gap: 6px; }
.blocked-box li { color: #7f1d1d; font-size: 12px; line-height: 1.75; }

.early-banner { margin: 12px 0; padding: 12px 14px; border: 1px solid #fcd34d; border-radius: 12px; background: #fffbeb; }
.early-banner > header { display: flex; flex-direction: column; gap: 2px; }
.early-banner > header b { font-size: 13px; color: #92400e; }
.early-banner > header small { color: #a16207; font-size: 11px; }
.early-reason { margin: 8px 0 0; font-size: 13px; line-height: 1.7; }
.early-banner details { margin-top: 8px; font-size: 12px; }
.early-banner summary { cursor: pointer; color: #92400e; }
.early-banner ul { margin: 8px 0 0; padding-left: 18px; display: grid; gap: 5px; }
.early-banner li { line-height: 1.6; }
.early-step { display: inline-block; margin-right: 6px; padding: 1px 6px; border-radius: 9px; background: #fef3c7; color: #92400e; font-size: 11px; }
.early-banner em { margin-left: 6px; color: var(--muted); font-style: normal; }

.step-fields { display: grid; gap: 8px; margin: 10px 0 8px; padding: 10px 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--white); }
.step-fields > b { font-size: 12px; }
.step-fields-scope { margin-top: -4px; color: var(--muted); font-size: 11px; }
/*
  字段格子（0828「框有大有小」的主因）：格子默认 stretch，
  于是带说明文字的那一格把整行撑高，同行没有说明的输入框跟着长高一截——
  截图里「零件变更前标识」比「实际断点」高一大块就是这么来的。
  对齐到顶 + 控件固定高度，一行里就都一样了。
*/
.step-fields-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px 12px; align-items: start; }
@media (max-width: 760px) { .step-fields-grid { grid-template-columns: repeat(2, 1fr); } }
.step-fields-grid input,
.step-fields-grid select { box-sizing: border-box; width: 100%; height: 34px; padding: 0 9px; border: 1px solid var(--line); border-radius: 6px; font-size: 13px; font-family: inherit; background: #fff; }
.step-fields-grid textarea { box-sizing: border-box; width: 100%; min-height: 68px; padding: 7px 9px; border: 1px solid var(--line); border-radius: 6px; font-size: 13px; font-family: inherit; }
.step-fields-grid input:focus,
.step-fields-grid select:focus,
.step-fields-grid textarea:focus { outline: 2px solid rgba(29, 78, 216, .18); outline-offset: 0; border-color: #93c5fd; }
.step-field { display: grid; gap: 4px; font-size: 12px; }
.step-field > span { color: var(--muted); font-size: 11px; }
.step-field.wide { grid-column: 1 / -1; }
.step-field small { color: var(--muted); font-size: 11px; line-height: 1.5; }
.step-field > span { min-height: 16px; }
.step-fields > .button { justify-self: start; }
.block-attach { display: grid; gap: 5px; }
.auth-attach-list { margin: 0; padding: 0; list-style: none; display: grid; gap: 4px; }
.auth-attach-list li {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 8px; border: 1px solid var(--line); border-radius: 8px; background: #fff;
}
.auth-attach-list a { font-size: 12px; }
.auth-attach-list small { color: var(--muted); font-size: 11px; }
.auth-attach-remove { margin-left: auto; color: #b45309; border-color: #fde68a; }
.auth-attach-remove:disabled { color: var(--muted); border-color: var(--line); }
.block-attach input { font-size: 12px; }
.block-attach small { color: var(--muted); font-size: 11px; line-height: 1.6; }
/* 新建态的第 1 步表单区：和步骤详情一个观感 */
.change-step-draft { margin-top: 10px; }
/* 已关闭时按钮撤掉，留一句状态——摆一颗点了报错的按钮不如不摆 */
.step-closed { color: #2e9e6b; font-size: 12px; font-weight: 700; white-space: nowrap; }
</style>
